import { useState } from 'react';
import { Skeleton, SkeletonizedValue } from '@round/ui-kit';
import { assets, Tooltip } from '@round/whitelabel-ui';
import { creatorbase } from '@round/api';
import PartnerClientAvatar from 'Modules/Plans/Partnerships/components/PartnerClientAvatar/PartnerClientAvatar';
import MoveStep from './components/MoveStep/MoveStep';
import UnlinkStep from './components/UnlinkStep/UnlinkStep';
import useLinkedProjectsPopoutData from './useLinkedProjectsPopoutData';
import popoutStyles from '../../popout.module.css';
import styles from './LinkedProjectsPopout.module.css';

const { ArrowRight: MoveIcon, LinkBroken: UnlinkIcon } = assets;

const LOADING_ROW_COUNT = 2;

type Step =
    | { name: 'list' }
    | { name: 'move'; share: creatorbase.ProjectShare }
    | { name: 'unlink'; share: creatorbase.ProjectShare };

type Props = {
    projectId: number;
    onClose: () => void;
};

const LinkedProjectsPopout = ({ projectId, onClose }: Props) => {
    const { shares, areSharesLoading, partnerships, arePartnershipsLoading } = useLinkedProjectsPopoutData(projectId);

    const [step, setStep] = useState<Step>({ name: 'list' });
    const handleBack = () => setStep({ name: 'list' });

    if (step.name === 'move') {
        return (
            <MoveStep
                share={step.share}
                agencyName={getAgency(partnerships, step.share)?.agency_name}
                currentProjectId={projectId}
                onBack={handleBack}
                onDone={onClose}
            />
        );
    }

    if (step.name === 'unlink') {
        return (
            <UnlinkStep
                share={step.share}
                agencyName={getAgency(partnerships, step.share)?.agency_name}
                onBack={handleBack}
                onDone={onClose}
            />
        );
    }

    return (
        <>
            <div className={popoutStyles.header}>
                <p className={popoutStyles.title}>Linked projects</p>
            </div>
            <ul className={styles.list}>
                {areSharesLoading &&
                    Array.from({ length: LOADING_ROW_COUNT }, (_, index) => (
                        <li key={index} className={styles.row} aria-busy>
                            <PartnerClientAvatar className={styles.agencyAvatar} name="" isLoading />
                            <div className={styles.titles}>
                                <Skeleton width="60%" />
                                <Skeleton width="40%" />
                            </div>
                        </li>
                    ))}
                {shares.map((share) => {
                    const agency = getAgency(partnerships, share);
                    return (
                        <li key={share.id} className={styles.row}>
                            <PartnerClientAvatar
                                className={styles.agencyAvatar}
                                name={agency?.agency_name ?? '-'}
                                logo={agency?.agency_logo}
                                isLoading={arePartnershipsLoading}
                            />
                            <div className={styles.titles}>
                                <p className={styles.name}>{share.project_name}</p>
                                <p className={styles.agency}>
                                    <SkeletonizedValue isInitialized={!arePartnershipsLoading}>
                                        {agency?.agency_name ?? '-'}
                                    </SkeletonizedValue>
                                </p>
                            </div>
                            <div className={styles.rowActions}>
                                <RowAction label="Move" onClick={() => setStep({ name: 'move', share })}>
                                    <MoveIcon className={styles.actionIcon} />
                                </RowAction>
                                <RowAction label="Unlink" onClick={() => setStep({ name: 'unlink', share })}>
                                    <UnlinkIcon className={styles.actionIcon} />
                                </RowAction>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

export default LinkedProjectsPopout;

const getAgency = (partnerships: creatorbase.Partnership[], share: creatorbase.ProjectShare) =>
    partnerships.find((partnership) => partnership.id === share.partnership_id);

type RowActionProps = {
    label: string;
    onClick: () => void;
    children: React.ReactNode;
};

const RowAction = ({ label, onClick, children }: RowActionProps) => {
    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

    return (
        <>
            <button ref={setAnchor} type="button" className={styles.rowAction} aria-label={label} onClick={onClick}>
                {children}
            </button>
            <Tooltip anchorElement={anchor}>
                <Tooltip.Body>{label}</Tooltip.Body>
            </Tooltip>
        </>
    );
};
